import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { skipToken } from "@reduxjs/toolkit/query";
import { loginApi } from "apis/eduidLogin";
import { signupApi } from "apis/eduidSignup";
import { RegisterEmail } from "components/Signup/SignupEmailForm";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { useEffect } from "react";
import { useParams } from "react-router";
import { signupSlice } from "slices/Signup";
import { ProcessCaptcha, SignupCaptcha } from "./SignupCaptcha";
import { SignupCredentials } from "./SignupCredentials";
import { SignupCredentialsError } from "./SignupCredentialsError";
import { ProcessEmailCode, SignupEnterCode } from "./SignupEnterCode";
import { SignupEntry } from "./SignupEntry";
import { ProcessToU, SignupToU } from "./SignupToU";
import { SignupUserCreated } from "./SignupUserCreated";
export function SignupApp() {
    const next_page = useAppSelector((state) => state.signup.next_page);
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (!next_page) {
            dispatch(signupSlice.actions.setNextPage("SIGNUP_START"));
        }
    }, [dispatch, next_page]);
    return (_jsxs(_Fragment, { children: [next_page === "SIGNUP_START" && _jsx(SignupStart, {}), next_page === "SIGNUP_ENTRY" && _jsx(SignupEntry, {}), next_page === "PROCESS_CAPTCHA" && _jsx(ProcessCaptcha, {}), next_page === "SIGNUP_CAPTCHA" && _jsx(SignupCaptcha, {}), next_page === "SIGNUP_TOU" && _jsx(SignupToU, {}), next_page === "PROCESS_TOU" && _jsx(ProcessToU, {}), next_page === "REGISTER_EMAIL" && _jsx(RegisterEmail, {}), next_page === "SIGNUP_ENTER_CODE" && _jsx(SignupEnterCode, {}), next_page === "PROCESS_EMAIL_CODE" && _jsx(ProcessEmailCode, {}), next_page === "SIGNUP_CREDENTIALS_ERROR" && _jsx(SignupCredentialsError, {}), next_page === "SIGNUP_CREDENTIALS" && _jsx(SignupCredentials, {}), next_page === "SIGNUP_USER_CREATED" && _jsx(SignupUserCreated, {})] }));
}
function getExternalMfaNextPage(state) {
    var _a, _b, _c, _d;
    if (((_a = state.tou) === null || _a === void 0 ? void 0 : _a.completed) && ((_b = state.email) === null || _b === void 0 ? void 0 : _b.address))
        return "SIGNUP_ENTER_CODE";
    if ((_c = state.tou) === null || _c === void 0 ? void 0 : _c.completed)
        return "SIGNUP_ENTRY";
    if (!((_d = state.email) === null || _d === void 0 ? void 0 : _d.address))
        return "SIGNUP_ENTRY";
    return "SIGNUP_TOU";
}
function getEmailNextPage(state) {
    var _a;
    if (!state.captcha.completed)
        return "SIGNUP_CAPTCHA";
    if (!((_a = state.tou) === null || _a === void 0 ? void 0 : _a.completed))
        return "SIGNUP_TOU";
    return "SIGNUP_ENTER_CODE";
}
function getNextPage(state) {
    var _a, _b, _c;
    if (state.already_signed_up)
        return null;
    if ((_a = state.email) === null || _a === void 0 ? void 0 : _a.completed)
        return "SIGNUP_CREDENTIALS";
    if ((_b = state.external_mfa) === null || _b === void 0 ? void 0 : _b.completed)
        return getExternalMfaNextPage(state);
    if ((_c = state.email) === null || _c === void 0 ? void 0 : _c.address)
        return getEmailNextPage(state);
    return "SIGNUP_ENTRY";
}
/**
 * Startup state to determine what kind of signup this is, and what to do next.
 */
function SignupStart() {
    const is_configured = useAppSelector((state) => state.config.is_configured);
    const loginRef = useAppSelector((state) => state.login.ref);
    const service_info = useAppSelector((state) => state.login.service_info);
    const params = useParams();
    const urlRef = params.ref;
    // bootstrap signup state in redux store by asking the backend for it when configuration is done
    const { data } = signupApi.useFetchStateQuery(is_configured ? undefined : skipToken);
    const [fetchLogout] = loginApi.useLazyFetchLogoutQuery();
    const [signupReturnToAuthn] = signupApi.useLazySignupReturnToAuthnQuery();
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (!is_configured || !data)
            return;
        // Check Redux first, then fall back to URL query parameter (survives refresh)
        const ref = loginRef || urlRef;
        if (ref) {
            signupReturnToAuthn({ ref: ref });
        }
    }, [is_configured, data, loginRef, urlRef, signupReturnToAuthn, service_info]);
    useEffect(() => {
        if (data !== undefined) {
            const nextPage = getNextPage(data.payload.state);
            if (nextPage === null) {
                fetchLogout({});
                dispatch(signupSlice.actions.resetState());
                dispatch(signupSlice.actions.setNextPage("SIGNUP_ENTRY"));
            }
            else {
                dispatch(signupSlice.actions.setNextPage(nextPage));
            }
        }
    }, [data, fetchLogout, dispatch]);
    return null;
}
